import { AxiosRequestConfig } from 'axios';

import API from '../constants/apiEndpoints';
import { IinstallBase } from '../models';

/**
 * Builds the offline request effect for a bulk install-base mutation.
 *
 * A bulk delete routes through the dedicated BulkDelete endpoint (IDs only, no
 * server-side Verify() that can 412 the whole batch), mirroring the Web dashboard.
 * Every other bulk edit PUTs the full install objects to UpdateInstalls.
 */
export const buildBulkInstallEffect = (
    isBulkDelete: boolean,
    updatedInstalls: IinstallBase[]
): AxiosRequestConfig =>
    isBulkDelete
        ? {
              url: API.inventory.bulkDelete,
              method: 'post',
              data: { installBaseIDs: updatedInstalls.map(install => install.id) }
          }
        : {
              url: API.inventory.updateInstalls,
              method: 'put',
              data: updatedInstalls
          };

const locationGroupKeys = ['buildingID', 'floorID', 'locationID', 'roomID'];

/**
 * Clears the location group through to the asset for a single-selection bulk edit.
 *
 * MMG-4200: the Edit Install form is pre-filled from the asset when exactly one is
 * selected, so its location group is authoritative and a location the user cleared
 * must persist rather than being dropped as "not filled out".
 *
 * MMG-4442: only that form submits the location group. Every other bulk mutation is
 * a partial change that never mentions location - Change Product sends { materialID },
 * a delete sends { isDeleted } - and nulling the group for those wiped the asset's
 * building/floor/location/room. So only clear through the keys the caller actually
 * submitted; an absent key leaves the asset's existing location alone.
 */
export const applySubmittedLocationGroup = (
    definedFormValues: { [key: string]: any },
    formValues: { [key: string]: any }
): { [key: string]: any } => {
    const applied = { ...definedFormValues };
    locationGroupKeys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(formValues, key)) {
            applied[key] = formValues[key] || null;
        }
    });
    return applied;
};

/**
 * Clears the system through to the asset for a single-selection bulk edit.
 *
 * Same reasoning as applySubmittedLocationGroup: the Edit Install form is authoritative
 * when one asset is selected, but a cleared system arrives as 0 (the API's "no system")
 * and is dropped as "not filled out". Only applied when the caller submitted it.
 */
export const applySubmittedSystem = (
    definedFormValues: { [key: string]: any },
    formValues: { [key: string]: any }
): { [key: string]: any } =>
    Object.prototype.hasOwnProperty.call(formValues, 'system')
        ? { ...definedFormValues, system: formValues.system ?? 0 }
        : definedFormValues;
